/**
 * @module util/portPicker/geo
 * Mode-agnostic geo helpers for the picker. Pure, no React — unit-testable in isolation.
 */

const EARTH_RADIUS_KM = 6371;
const toRadians = (deg) => (deg * Math.PI) / 180;
const isFiniteNum = (n) => typeof n === 'number' && Number.isFinite(n);

/** Coerce a coord that may arrive as a Betty Decimal string ("29.883333") → number|null. */
export const toCoord = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return isFiniteNum(n) ? n : null;
};

/** True when an object has both a usable latitude and longitude. */
export const hasCoords = (p) => p != null && toCoord(p.lat ?? p.latitude) != null && toCoord(p.lng ?? p.longitude) != null;

const lat = (p) => toCoord(p.lat ?? p.latitude);
const lng = (p) => toCoord(p.lng ?? p.longitude);

/**
 * Great-circle distance in kilometres between two {lat,lng} (or {latitude,longitude})
 * points. Returns null when either point lacks usable coordinates.
 */
export const haversineKm = (a, b) => {
  if (!hasCoords(a) || !hasCoords(b)) return null;
  const lat1 = lat(a);
  const lat2 = lat(b);
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng(b) - lng(a));
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

/** Round a distance for display (whole km). */
export const roundKm = (km) => (km == null ? null : Math.round(km));
